import { create } from "zustand";

import { readStored, writeStored } from "../lib/persisted";

/**
 * How wide the host has dragged the console's side columns.
 *
 * ## Why `null` is a value here
 *
 * Null means "never dragged", and it is not the same as a number that happens
 * to match the default. Undragged columns use `clamp(380px, 29vw, 520px)` - they
 * grow with the viewport and stop - which is better than any fixed number for
 * somebody who has not expressed an opinion, because the same console is opened
 * on a laptop and on a 34-inch display.
 *
 * Storing a pixel width the first time somebody drags means their choice wins
 * from then on, and the responsive default stays for everybody who never
 * touches it. Double-clicking a divider puts it back to null.
 *
 * ## Why widths and not fractions
 *
 * The middle column is what absorbs the difference (`minmax(0, 3fr)`), so the
 * sides are the things being sized. A host drags until the preview looks right
 * or the queue fits, and both of those are judgements about a real size on a
 * real screen - not about a proportion of a window they might resize later.
 */
interface ConsoleState {
  /** The studio column, in pixels. Null until dragged. */
  studioWidth: number | null;
  /** The chat and listeners rail, in pixels. Null until dragged. */
  railWidth: number | null;

  /**
   * Which half of the studio settings is showing.
   *
   * Persisted, and that is the point. The mix faders live under `sound` and
   * they are the controls a host reaches for *during* a set - so a tab that
   * reset to `frame` on every navigation would put the two things they touch
   * most behind a click, every time, for ever.
   *
   * `frame` is the right first answer for somebody who has never chosen: the
   * first thing anybody does after opening the studio is arrange the picture.
   */
  studioTab: "frame" | "sound";

  /**
   * How this host publishes: from the catalogue, from the browser studio, or
   * from an encoder of their own.
   *
   * ## Why it is remembered rather than derived
   *
   * The room only knows whether it is playing its catalogue or carrying a live
   * stream - and the browser studio and OBS produce the *same* live stream, into
   * the same Cloudflare input, arriving at listeners identically. Nothing
   * downstream can tell them apart, which is a good property and means the
   * distinction exists only in this console.
   *
   * So it is stored here, and reconciled against the room: a room live from the
   * catalogue is `audio` whatever this says.
   */
  broadcastMode: "audio" | "studio" | "external";

  setWidth: (pane: "studioWidth" | "railWidth", width: number | null) => void;
  setStudioTab: (tab: "frame" | "sound") => void;
  setBroadcastMode: (mode: "audio" | "studio" | "external") => void;
}

const STORAGE_KEY = "gv.console";

/**
 * The narrowest each side may be dragged.
 *
 * The studio's floor is higher than the rail's on purpose: it holds a video
 * preview, and a host judging their own framing in a box narrower than this is
 * being asked to trust something they cannot really see. The rail is text and
 * survives being thin.
 */
export const PANE_LIMITS = {
  studioWidth: { min: 320, max: 720 },
  railWidth: { min: 280, max: 640 },
} as const;

/** What the middle column keeps whatever the sides are dragged to. */
export const QUEUE_MIN = 360;

/**
 * What an undragged column is, expressed once.
 *
 * These are the numbers in the `clamp()` the CSS uses, and they are here rather
 * than inline in the console because two places need to agree about them: the
 * grid, which paints the column, and the resizer, which has to know how wide a
 * *neighbouring* column is when that neighbour has never been dragged and so has
 * no stored width to read.
 *
 * They were briefly duplicated, and the copies disagreed on the viewport
 * fraction within the hour.
 */
export const PANE_DEFAULTS = {
  studioWidth: { min: 380, vw: 0.29, max: 520 },
  railWidth: { min: 340, vw: 0.26, max: 460 },
} as const;

/** The CSS for an undragged column. */
export function defaultCss(pane: keyof typeof PANE_DEFAULTS): string {
  const d = PANE_DEFAULTS[pane];

  return `clamp(${d.min}px, ${d.vw * 100}vw, ${d.max}px)`;
}

/** What that `clamp()` resolves to at a given container width. */
export function defaultWidth(
  pane: keyof typeof PANE_DEFAULTS,
  container: number,
): number {
  const d = PANE_DEFAULTS[pane];

  return Math.min(d.max, Math.max(d.min, container * d.vw));
}

function restore(): Pick<
  ConsoleState,
  "studioWidth" | "railWidth" | "studioTab" | "broadcastMode"
> {
  const saved = readStored<ConsoleState>(STORAGE_KEY);

  /*
   * Clamped on the way in, not just on the way out.
   *
   * A width written on a wide display can be wider than the whole viewport on a
   * laptop, and a stored value from a build with different limits can be
   * anything at all. Neither should produce a console with no middle column and
   * no way back other than clearing site data.
   */
  const width = (value: unknown, limits: { min: number; max: number }) =>
    typeof value === "number" && Number.isFinite(value)
      ? Math.min(limits.max, Math.max(limits.min, value))
      : null;

  return {
    studioWidth: width(saved.studioWidth, PANE_LIMITS.studioWidth),
    railWidth: width(saved.railWidth, PANE_LIMITS.railWidth),
    studioTab: saved.studioTab === "sound" ? "sound" : "frame",
    broadcastMode:
      saved.broadcastMode === "studio" || saved.broadcastMode === "external"
        ? saved.broadcastMode
        : "audio",
  };
}

export const useConsoleLayout = create<ConsoleState>()((set) => ({
  ...restore(),

  setWidth: (pane, width) => set({ [pane]: width } as Partial<ConsoleState>),
  setStudioTab: (studioTab) => set({ studioTab }),
  setBroadcastMode: (broadcastMode) => set({ broadcastMode }),
}));

useConsoleLayout.subscribe((state) => {
  writeStored(STORAGE_KEY, {
    studioWidth: state.studioWidth,
    railWidth: state.railWidth,
    studioTab: state.studioTab,
    broadcastMode: state.broadcastMode,
  });
});
